import {useCallback, useEffect, useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {DocumentUpdateSpec, EventConstants} from 'constants/EventConstants';
import DocumentActions, {
  type DocumentVersionSelector,
  LATEST_DOCUMENT_VERSION,
} from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {getDocumentSaveState} from 'notebook/documents/useDocumentSaveState';
import {EntryHistoryState, HistoryState} from 'notebook/ELNConstants';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import DocumentUpdatesManager from 'notebook/realtime/DocumentUpdatesManager';
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';

export interface UseDocument {
  getIdFromCid: (cid: string) => string | number | undefined;
  historyManager: HistoryManager<HistoryState<Note>>;
  isLatest: boolean;
  note?: Note;
  isDirty: boolean;
  parentType: string;
  registerObjects: (cidIdMap: unknown) => void;
  updateNote: (
    note: Note,
    options?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    },
    structuredTableMappings?: Array<StructuredTableMapping>,
    relatedDocuments?: {[documentId: string]: Note}
  ) => void;
  updateTitle: (title: string) => void;
  restoreStateAndSelection: (state: EntryHistoryState, selectionData: SelectionRestoreData) => void;
}

export default function useDocument(
  documentId: string,
  documentVersionId: DocumentVersionSelector,
  disableAutoSaver?: boolean,
  onChange?: (wasServerUpdate: boolean, changedDocumentId: string) => void,
  customSaveHandler?: () => Promise<boolean>,
  manageRealtimeUpdates: boolean = false,
  getRelatedDocumentIds?: () => Array<string>
): UseDocument {
  const historyManager = useMemo(() => new HistoryManager<HistoryState<Note>>(), []);
  const documentUpdatesManager = useMemo(
    () => (manageRealtimeUpdates ? new DocumentUpdatesManager() : null),
    [manageRealtimeUpdates]
  );

  const parentType = 'Note';

  const {isLatest, latestVersionId, isDirty} = getDocumentSaveState(documentId);

  const registerObjects: (cidIdMap: unknown) => void = useCallback(
    (cidIdMap: CidIdMap) => {
      DocumentActions.registerObjects(documentId, cidIdMap);
    },
    [documentId]
  );

  const getIdFromCid: (cid: string) => string | number | undefined = useCallback(
    (cid: string) => DocumentStore.getIdFromCid(documentId, cid),
    [documentId]
  );

  const updateNote = useCallback(
    (
      note: Note,
      options?: {
        performTableEvaluation?: boolean;
        updatedItemPartIndexes?: Array<number>;
        canIncrementalSave?: boolean;
      },
      structuredTableMappings?: Array<StructuredTableMapping>,
      updatedRelatedDocuments?: {[documentId: string]: Note}
    ): void => {
      if (updatedRelatedDocuments && BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
        const relatedDocumentSpecs: Array<DocumentUpdateSpec> = Object.entries(updatedRelatedDocuments).map(
          ([documentId, relatedNote]) => ({
            id: documentId,
            spec: DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(relatedNote)),
          })
        );
        DocumentActions.updateDocumentGroup(
          {
            id: documentId,
            spec: DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(note)),
          },
          relatedDocumentSpecs,
          {
            structuredTableMappings,
            ...options,
          }
        );
      } else {
        DocumentActions.updateNote(documentId, DocumentUtil.toEntryNote(note), {
          structuredTableMappings,
          ...options,
        });
      }
    },
    [documentId]
  );

  const updateTitle: (title: string) => void = useCallback(
    (name: string) => {
      DocumentActions.setName(documentId, name);
    },
    [documentId]
  );

  const restoreStateAndSelection = useCallback(
    (state: EntryHistoryState, selectionData: SelectionRestoreData): void => {
      DocumentActions.revert(
        documentId,
        {
          name: {$set: state.title},
          ...DocumentUtil.entryVersionUpdate({$merge: {note: state.note}}),
        },
        selectionData
      );
    },
    [documentId]
  );

  useEffect(() => {
    async function openDocument(): Promise<void> {
      const shouldOpenLatestVersion = documentVersionId === LATEST_DOCUMENT_VERSION;
      // Reset history whenever version selection changes to avoid stale undo/redo state bleed.
      historyManager.reset();
      const document = DocumentStore.getDocument(documentId);
      if (getRelatedDocumentIds) {
        DocumentStore.setRelatedDocumentIdGetter(documentId, getRelatedDocumentIds);
      }
      if (!shouldOpenLatestVersion) {
        if (document) {
          DocumentActions.close(documentId);
        }
        await DocumentActions.open(documentId, {documentVersionId});
        return;
      }
      if (!document) {
        await DocumentActions.open(documentId, {
          documentVersionId,
          disableAutoSaver,
          relatedDocumentIdGetter: BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
            ? getRelatedDocumentIds
            : undefined,
        });
      } else if (document.isStale) {
        await DocumentActions.refresh(documentId);
      }

      if (disableAutoSaver) {
        DocumentStore.disableAutoSaver(documentId);
      } else {
        DocumentStore.enableAutoSaver(documentId);
      }
      DocumentStore.setCustomSaveHandler(documentId, customSaveHandler);
    }
    openDocument();
    // openDocument, as it's name suggests, should only enter the stack trace when documentId or
    // documentVersionId changes. This means each of: getRelatedDocumentIds, customSaveHandler,
    // historyManager, & disableAutoSaver should NOT be asserted in the following dependency list.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, documentVersionId]);

  useEffect(() => {
    if (!DocumentStore.inStore(documentId)) {
      return;
    }
    DocumentStore.setCustomSaveHandler(documentId, customSaveHandler);
  }, [documentId, customSaveHandler]);

  useEffect(() => {
    if (documentVersionId !== LATEST_DOCUMENT_VERSION) {
      return;
    }
    if (!DocumentStore.inStore(documentId)) {
      return;
    }
    if (disableAutoSaver) {
      DocumentStore.disableAutoSaver(documentId);
    } else {
      DocumentStore.enableAutoSaver(documentId);
    }
  }, [documentId, documentVersionId, disableAutoSaver]);

  /**
   * Add a listener for document updates to trigger the onChange callback.
   *
   * Fire onchange whenever the store signals that this document (or a related document)
   * has unsaved changes. This automatically covers any current or future event type that bumps
   * openVersion — including DOCUMENT_UPDATE_RELATED_DOCUMENTS,
   * EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE, EXCESS_FORCE_EVALUATION with
   * bumpVersion:true, etc.
   *
   * DOCUMENT_SERVER_SIDE_UPDATE is the single explicit exception: server-side updates
   * do not bump openVersion (the document is refreshed from the server), so they must
   * be detected by event type rather than by hasUnsavedChanges.
   */
  useEffect(() => {
    const callback = onChange
      ? (context: {id?: string; actionType: string}): void => {
          const {id, actionType} = context;
          const relatedDocumentIds = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
            ? DocumentStore.getRelatedDocumentIds(documentId)
            : [];
          if (
            (documentId === id || (id != null && relatedDocumentIds?.includes(id))) &&
            DocumentStore.inStore(documentId)
          ) {
            /**
             * Server-side updates must always trigger onChange regardless of unsaved-change state,
             * because they refresh the document from the server without bumping openVersion.
             * All other content-modifying events are detected via DocumentStore.hasUnsavedChanges.
             */
            const isServerUpdate = actionType === EventConstants.DOCUMENT_SERVER_SIDE_UPDATE;
            if (isServerUpdate || DocumentStore.hasUnsavedChanges(documentId)) {
              onChange(isServerUpdate, id);
            }
          }
        }
      : null;
    if (callback) {
      DocumentStore.addChangeListener(callback);
    }
    return () => {
      if (callback) {
        DocumentStore.removeChangeListener(callback);
      }
    };
  }, [documentId, onChange]);

  /**
   * Add a listener for server side updates to trigger a reload of the history manager
   */
  useEffect(() => {
    const onServerSideUpdate = (context: {id?: string; actionType: string}): void => {
      const {id, actionType} = context;
      if (
        documentId === id &&
        DocumentStore.inStore(documentId) &&
        actionType === EventConstants.DOCUMENT_SERVER_SIDE_UPDATE
      ) {
        historyManager.reset();
      }
    };
    DocumentStore.addChangeListener(onServerSideUpdate);
    return () => {
      DocumentStore.removeChangeListener(onServerSideUpdate);
    };
  }, [documentId, historyManager]);

  useEffect(() => {
    documentUpdatesManager?.changeActiveDocument(documentId);
    return () => {
      documentUpdatesManager?.markInactive();
    };
  }, [documentId, documentUpdatesManager]);

  /**
   * Load the latest version if it is different from the current version
   */
  useEffect(() => {
    if (
      documentVersionId === LATEST_DOCUMENT_VERSION &&
      latestVersionId !== undefined &&
      (!DocumentStore.getDocument(documentId) ||
        (!DocumentStore.hasUnsavedChanges(documentId) &&
          DocumentStore.getVersionState(documentId)?.openVersionId !== latestVersionId))
    ) {
      DocumentActions.loadVersion(documentId, latestVersionId);
      historyManager.reset();
    }

    // Release lock on unmount; evict pinned versions from store to avoid stale content
    return () => {
      DocumentEditorLockUtil.releaseLocks([documentId]);
      if (documentVersionId !== LATEST_DOCUMENT_VERSION) {
        DocumentActions.close(documentId);
      }
    };
    // We don't want to reload every time the latest version updates, only when we change to a different doc
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, documentVersionId]);

  return {
    getIdFromCid,
    historyManager,
    isLatest,
    isDirty,
    parentType,
    registerObjects,
    updateNote,
    updateTitle,
    restoreStateAndSelection,
  };
}
